import React, { useState, useMemo, useEffect, useRef } from 'react';
import AttendanceRow from '../components/attendence/AttendenceRow';
import BadgeCarousel from '../components/attendence/BadgeCarousel';
import { attendanceBadges } from '../utils/attendanceBadges';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronLeft, ChevronRight } from "lucide-react";
import toast from 'react-hot-toast';

// Hooks
import { useChildren, useApi } from '../hooks';
import { useAuth } from '../context/auth';

// Services
import { attendanceService, badgesService, enrollmentsService, classesService } from '../api/services';

// Fallback icons
import icon1 from "../assets/Mask group.png";
import icon2 from '../assets/Mask group (1).png';
import icon3 from '../assets/Mask group (2).png';
import icon4 from '../assets/Mask group (3).png';
import icon5 from '../assets/Mask group (4).png';

import { sortClassesByName } from "../utils/format";
import { classesOf } from "../utils/classGroups";
import useClassGroups from "../hooks/useClassGroups";
const Attendence = () => {
  // Get user role from auth context
  const { user } = useAuth();
  const role = localStorage.getItem('role');
  const isCoach = role === 'coach';

  // ============================================================================
  // PARENT-SPECIFIC DATA FETCHING
  // ============================================================================
  // Get selected child from context (only for parent role)
  const { selectedChild } = useChildren();

  // Get first active enrollment for the selected child (parent only)
  const { data: enrollmentsData, error: enrollmentsError } = useApi(
    () => enrollmentsService.getMy({
      child_id: selectedChild?.id,
      status: 'active',
    }),
    {
      initialData: [],
      dependencies: [selectedChild?.id],
      autoFetch: !isCoach && !!selectedChild?.id,
      onError: (err) => console.error('Failed to load enrollments:', err),
    }
  );

  const firstEnrollment = useMemo(() => {
    if (isCoach) return null;
    if (!enrollmentsData || enrollmentsData.length === 0) return null;
    return enrollmentsData.find(e => e.child_id === selectedChild?.id) || enrollmentsData[0];
  }, [enrollmentsData, selectedChild?.id, isCoach]);

  // ============================================================================
  // COACH-SPECIFIC DATA FETCHING
  // ============================================================================
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch coach's assigned classes
  const { data: classesData, loading: loadingClasses } = useApi(
    () => classesService.getAll({ coach_id: user?.id }),
    {
      initialData: { items: [] },
      dependencies: [user?.id],
      autoFetch: isCoach && !!user?.id,
    }
  );

  // Extract classes array
  const classes = useMemo(() => {
    if (!isCoach) return [];
    return sortClassesByName(
      Array.isArray(classesData) ? classesData : classesData?.items || [],
    );
  }, [classesData, isCoach]);

  // One sheet for every membership of a TDC site, listed above the classes
  const classGroups = useClassGroups(classes);
  const classOptions = useMemo(
    () => [...classGroups, ...classes],
    [classGroups, classes],
  );

  // Set first class as default when classes load
  useEffect(() => {
    if (isCoach && classes?.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
    }
  }, [classes, selectedClass, isCoach]);

  // Which class+date roster is on screen. The skeleton is only for a
  // roster's FIRST load; the refetch after marking a student must keep the
  // list mounted, or every tap flashes the roster and throws the coach back
  // to the top of the page (same defect as the check-in page, 2026-09-15).
  const [loadedRosterKey, setLoadedRosterKey] = useState(null);
  const requestedRosterKeyRef = useRef(null);
  requestedRosterKeyRef.current = `${selectedClass?.id || ''}|${selectedDate}`;

  // Fetch class attendance for coach
  const { data: classAttendanceData, loading: loadingClassAttendance, error: classAttendanceError, refetch: refetchClassAttendance } = useApi(
    // A combined sheet is every member class's roster, each child tagged with
    // their own class. Promise.all on purpose: one failed roster must show as
    // an error, not as a shorter list.
    () =>
      Promise.all(
        classesOf(selectedClass, selectedDate).map(async (cls) => {
          const data = await attendanceService.getByClass(cls.id, selectedDate);
          const records = Array.isArray(data) ? data : data?.items || [];
          return records.map((record) => ({
            ...record,
            class_id: cls.id,
            class_name: cls.name,
          }));
        }),
      ).then((rosters) => rosters.flat()),
    {
      initialData: [],
      dependencies: [selectedClass?.id, selectedDate],
      autoFetch: isCoach && !!selectedClass?.id,
      onSuccess: () => setLoadedRosterKey(requestedRosterKeyRef.current),
      onError: (err) => console.error('Failed to load class attendance:', err),
    }
  );
  const isFirstRosterLoad =
    loadingClassAttendance && loadedRosterKey !== requestedRosterKeyRef.current;

  // Coaches can now actually record attendance. This was previously a
  // useMutation over attendanceService.create — a route that did not exist —
  // whose return value was discarded, so it was bound to no control and could
  // never have fired.
  const [markingId, setMarkingId] = useState(null);

  const markAttendance = async (enrollmentId, status, classId) => {
    if (!classId) return;
    setMarkingId(enrollmentId);
    try {
      await attendanceService.markOne({
        // The child's own class: a combined sheet has no class of its own
        classId,
        date: selectedDate,
        enrollmentId,
        status,
      });
      await refetchClassAttendance();
    } catch (err) {
      console.error('Failed to mark attendance:', err);
      // Silence here read as "saved": the spinner stopped and nothing else.
      toast.error(err?.message || 'Could not save attendance. Please try again.');
    } finally {
      setMarkingId(null);
    }
  };

  // ============================================================================
  // PARENT-SPECIFIC DATA (continued)
  // ============================================================================
  // Fetch badges for the enrollment (parent only)
  const { data: badgesData, loading: loadingBadges, error: badgesError } = useApi(
    () => badgesService.getByEnrollment(firstEnrollment?.id),
    {
      initialData: [],
      dependencies: [firstEnrollment?.id],
      autoFetch: !isCoach && !!firstEnrollment?.id,
      onError: (err) => console.error('Failed to load badges:', err),
    }
  );

  // Fetch attendance history for the enrollment (parent only)
  const { data: attendanceData, loading: loadingAttendance, error: attendanceError } = useApi(
    () => attendanceService.getByEnrollment(firstEnrollment?.id),
    {
      initialData: null,
      dependencies: [firstEnrollment?.id],
      autoFetch: !isCoach && !!firstEnrollment?.id,
      onError: (err) => console.error('Failed to load attendance:', err),
    }
  );

  // Combined error state
  const hasError = isCoach
    ? classAttendanceError
    : (enrollmentsError || badgesError || attendanceError);

  // Only the badges the child earned (no demo fallback).
  const badges = useMemo(
    () => attendanceBadges(badgesData, [icon1, icon2, icon3, icon4, icon5]),
    [badgesData],
  );

  // Transform API attendance to component format
  const attendance = useMemo(() => {
    // Handle paginated response {items, total, skip, limit}
    const records = attendanceData?.items || [];
    console.log('Attendance Debug:', {
      selectedChild: selectedChild?.id,
      selectedChildName: selectedChild?.first_name,
      firstEnrollmentId: firstEnrollment?.id,
      attendanceData,
      recordsCount: records.length,
    });
    if (records.length > 0) {
      return records.map(record => ({
        date: new Date(record.date || record.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        status: record.status === 'present' ? 'Present' : record.status === 'absent' ? 'Absent' : record.status,
      }));
    }
    // Return empty array instead of demo data to show real state
    return [];
  }, [attendanceData, selectedChild, firstEnrollment]);

  // Pagination Logic
  const itemsPerPage = 4;
  const [page, setPage] = useState(1);

  // Reset pagination when enrollment changes
  useEffect(() => {
    setPage(1);
  }, [firstEnrollment?.id]);

  const totalPages = Math.ceil(attendance.length / itemsPerPage) || 1;

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedData = attendance.slice(startIndex, startIndex + itemsPerPage);

  const prevPage = () => page > 1 && setPage(page - 1);
  const nextPage = () => page < totalPages && setPage(page + 1);

  // ============================================================================
  // COACH VIEW - Transform class attendance to display format
  // ============================================================================
  const coachAttendance = useMemo(() => {
    if (!isCoach || !classAttendanceData) return [];
    const records = Array.isArray(classAttendanceData) ? classAttendanceData : (classAttendanceData?.items || []);
    return records.map(record => ({
      id: record.id,
      // The Present/Absent/Excused buttons are disabled without this; it was
      // never mapped, so they had been dead since they shipped (2026-08-26).
      enrollment_id: record.enrollment_id,
      classId: record.class_id || selectedClass?.id,
      // Shown on a combined sheet only, where it tells memberships apart
      className: selectedClass?.isGroup ? record.class_name : null,
      child_id: record.child_id,
      child_name: record.child_name || `${record.child?.first_name || ''} ${record.child?.last_name || ''}`.trim(),
      date: new Date(record.date || record.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      status: record.status === 'present' ? 'Present' : record.status === 'absent' ? 'Absent' : record.status,
      rawStatus: record.status,
    }));
  }, [classAttendanceData, isCoach, selectedClass]);

  // Loading state for coach
  const isCoachLoading = loadingClasses || isFirstRosterLoad;

  return (
    <div className="min-h-screen max-sm:h-fit bg-page-gradient max-sm:pb-20">
      <Header />

      <main className="px-6 py-8 max-xxl:py-5 max-sm:py-4 max-sm:px-3">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-fluid-2xl text-[#173151] font-kollektif font-normal leading-[1.002] tracking-[-0.02em]">
              Attendance
            </h1>
            {!isCoach && selectedChild && (
              <p className="text-sm text-gray-600 font-manrope mt-1">
                Showing for: {selectedChild.first_name} {selectedChild.last_name}
                {firstEnrollment?.class_name && ` - ${firstEnrollment.class_name}`}
              </p>
            )}
          </div>

          {/* Coach Controls: Class Selector + Date Picker */}
          {isCoach && (
            <div className="flex flex-wrap items-center gap-4 max-sm:gap-2 max-sm:w-full">
              {/* Date Picker */}
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="py-2 px-4 bg-white/50 border border-[#e1e1e1] rounded-full text-base font-medium font-manrope text-[#1B1B1B]"
              />

              {/* Class Selector */}
              {classOptions.length > 0 && (
                <div className="relative min-w-0 max-w-full max-sm:w-full">
                  <select
                    value={selectedClass?.id || ''}
                    onChange={(e) => {
                      const classItem = classOptions.find(c => c.id === e.target.value);
                      setSelectedClass(classItem);
                    }}
                    className="w-full max-w-full truncate py-2 px-4 bg-white/50 border border-[#e1e1e1] rounded-full text-base font-medium font-manrope text-[#1B1B1B] cursor-pointer appearance-none pr-10"
                  >
                    {classOptions.map((classItem) => (
                      <option key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </option>
                    ))}
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Alert */}
        {hasError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-manrope">
            <p className="font-medium">Unable to load attendance data</p>
            <p className="text-sm mt-1">Please try refreshing the page or check back later.</p>
          </div>
        )}

        {/* Parent View: Badge Carousel */}
        {!isCoach && (
          <BadgeCarousel badges={badges} loading={loadingBadges} compact={true} />
        )}

        {/* Attendance History Section */}
        <div className={`${!isCoach ? 'mt-8 max-xxl1:mt-3' : ''} bg-badge-bg rounded-3xl p-6 shadow-lg`}>
          <h2 className="font-kollektif text-xl my-4 font-normal text-fluid-md text-[#0f1d2e] mb-4">
            {isCoach ? `Attendance for ${selectedDate}` : 'Attendance History'}
          </h2>

          {/* Paginated Rows */}
          <div className="flex flex-col gap-4 max-xxl1:gap-2">
            {(isCoach ? isCoachLoading : loadingAttendance) ? (
              // Loading skeleton
              Array(4).fill(0).map((_, i) => (
                <div key={i} className="bg-white/50 rounded-xl p-4 animate-pulse flex justify-between items-center">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
              ))
            ) : isCoach ? (
              // Coach View: Show all students with attendance status
              coachAttendance.length > 0 ? (
                coachAttendance.map((item, i) => (
                  <div key={i} className="bg-white/50 rounded-xl p-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1D3557] rounded-full flex items-center justify-center text-white font-semibold">
                        {item.child_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <span className="font-medium text-[#173151]">{item.child_name}</span>
                        {item.className && (
                          <p className="text-xs font-manrope text-[#1D3557]">{item.className}</p>
                        )}
                      </div>
                    </div>
                    {/* Coaches can now set the status. Present/Absent/Excused
                        is why this reads the attendance table rather than
                        check-ins: a check-in cannot express "excused". */}
                    <div className="flex items-center gap-1.5">
                      {[
                        { value: 'present', label: 'Present', on: 'bg-green-600 text-white', off: 'bg-green-50 text-green-700 hover:bg-green-100' },
                        { value: 'absent', label: 'Absent', on: 'bg-red-600 text-white', off: 'bg-red-50 text-red-700 hover:bg-red-100' },
                        { value: 'excused', label: 'Excused', on: 'bg-gray-600 text-white', off: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
                      ].map((opt) => {
                        const active = item.rawStatus === opt.value;
                        const busy = markingId === item.enrollment_id;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={busy || !item.enrollment_id}
                            onClick={() => markAttendance(item.enrollment_id, opt.value, item.classId)}
                            aria-pressed={active}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium font-manrope transition disabled:opacity-50 ${active ? opt.on : opt.off}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="font-medium text-gray-600 mb-1">No attendance records</p>
                  <p className="text-sm">No attendance has been recorded for {selectedDate}</p>
                </div>
              )
            ) : (
              // Parent View: Paginated attendance history
              paginatedData.length > 0 ? (
                paginatedData.map((item, i) => (
                  <AttendanceRow key={i} {...item} />
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="font-medium text-gray-600 mb-1">No attendance records yet</p>
                  <p className="text-sm">Attendance will appear here once recorded</p>
                </div>
              )
            )}
          </div>

          {/* Pagination Controls (Parent only) */}
          {!isCoach && (
            <div className="flex justify-center items-center gap-3 mt-6 max-xxl1:mt-4">
              <button
                onClick={prevPage}
                disabled={page === 1}
                className={`p-2 rounded-full border ${
                  page === 1 ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                <ChevronLeft size={16} />
              </button>

              <span className="text-sm font-semibold">
                Page {page} / {totalPages}
              </span>

              <button
                onClick={nextPage}
                disabled={page === totalPages}
                className={`p-2 rounded-full border ${
                  page === totalPages ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer isFixed={true} mobileHidden={true} />
    </div>
  );
};

export default Attendence;
